export type KeySignatureScale = "major" | "minor";

export type KeySignatureOption = {
  id: string;
  display: string;
  major: {
    value: string;
    aliases: string[];
  };
  minor: {
    value: string;
    aliases: string[];
  };
};

type KeyDefinition = {
  id: string;
  display: string;
  canonical: string;
  aliases: string[];
};

const defineKey = (
  id: string,
  display: string,
  canonical: string,
  aliases: string[] = [],
): KeyDefinition => ({
  id,
  display,
  canonical,
  aliases: [canonical, ...aliases],
});

const KEY_DEFINITIONS: KeyDefinition[] = [
  defineKey("A", "A", "A"),
  defineKey("A#/Bb", "A#/Bb", "A♯", ["A#", "Bb", "B♭"]),
  defineKey("B", "B", "B"),
  defineKey("C", "C", "C"),
  defineKey("C#/Db", "C#/Db", "C♯", ["C#", "Db", "D♭"]),
  defineKey("D", "D", "D"),
  defineKey("D#/Eb", "D#/Eb", "D♯", ["D#", "Eb", "E♭"]),
  defineKey("E", "E", "E"),
  defineKey("F", "F", "F"),
  defineKey("F#/Gb", "F#/Gb", "F♯", ["F#", "Gb", "G♭"]),
  defineKey("G", "G", "G"),
  defineKey("G#/Ab", "G#/Ab", "G♯", ["G#", "Ab", "A♭"]),
];

const toMinorAlias = (alias: string) =>
  alias.trim().replace(/[A-G]/g, (char) => char.toLowerCase());

export const KEY_SIGNATURE_OPTIONS: KeySignatureOption[] = KEY_DEFINITIONS.map(
  ({ id, display, canonical, aliases }) => ({
    id,
    display,
    major: { value: canonical, aliases },
    minor: {
      value: canonical.toLowerCase(),
      aliases: aliases.map(toMinorAlias),
    },
  }),
);

type KeyLookupResult = {
  option: KeySignatureOption;
  scale: KeySignatureScale;
  canonicalValue: string;
};

const aliasIndex = new Map<string, KeyLookupResult>();

for (const option of KEY_SIGNATURE_OPTIONS) {
  option.major.aliases.forEach((alias) => {
    aliasIndex.set(alias, {
      option,
      scale: "major",
      canonicalValue: option.major.value,
    });
  });
  option.minor.aliases.forEach((alias) => {
    aliasIndex.set(alias, {
      option,
      scale: "minor",
      canonicalValue: option.minor.value,
    });
  });
}

function resolveKeyValue(value?: string | null): KeyLookupResult | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  return aliasIndex.get(trimmed) ?? null;
}

export function getCanonicalKeyValue(value?: string | null) {
  return resolveKeyValue(value)?.canonicalValue ?? null;
}

export function getKeyDisplayLabel(value?: string | null) {
  const result = resolveKeyValue(value);
  if (!result) {
    return value?.trim() ?? null;
  }

  return `${result.option.display} ${
    result.scale === "major" ? "Major" : "Minor"
  }`;
}

export function getKeyQueryPitches(value?: string | null) {
  const result = resolveKeyValue(value);
  if (!result) {
    return value ? [value] : undefined;
  }

  const aliases =
    result.scale === "major"
      ? result.option.major.aliases
      : result.option.minor.aliases;

  return Array.from(new Set(aliases));
}
